import { useEffect, useRef, useState } from 'react';

interface PersonaSectionsProps {
  personaSections: Array<{
    media: string;
    type: 'video' | 'image';
    title: string;
    tagline: string;
    description: string;
  }>;
  isMobile: boolean;
  getElementAnimation: (element: HTMLElement | null) => { opacity: number; transform: string };
}

export function PersonaSections({ personaSections, isMobile, getElementAnimation }: PersonaSectionsProps) {
  return (
    <>
      {personaSections.map((persona, index) => (
        <PersonaSection
          key={index}
          media={persona.media}
          type={persona.type}
          title={persona.title}
          tagline={persona.tagline}
          description={persona.description}
          index={index}
          isMobile={isMobile}
          getElementAnimation={getElementAnimation}
        />
      ))}
    </>
  );
}

interface PersonaSectionProps {
  media: string;
  type: 'video' | 'image';
  title: string;
  tagline: string;
  description: string;
  index: number;
  isMobile: boolean;
  getElementAnimation: (element: HTMLElement | null) => { opacity: number; transform: string };
}

const FADE_DELAY_MS = 3000;
const TRANSITION_DURATION = '0.8s';

function PersonaSection({
  media,
  type,
  title,
  tagline,
  description,
  index,
  isMobile,
  getElementAnimation,
}: PersonaSectionProps) {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const fadeTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          fadeTimerRef.current = setTimeout(() => setVisible(false), FADE_DELAY_MS);
        } else {
          setVisible(false);
          if (fadeTimerRef.current) clearTimeout(fadeTimerRef.current);
        }
      },
      { threshold: 0.5 },
    );

    observer.observe(el);
    return () => {
      observer.disconnect();
      if (fadeTimerRef.current) clearTimeout(fadeTimerRef.current);
    };
  }, []);

  return (
    <div
      ref={sectionRef}
      data-persona-section={index === 0 ? 'true' : undefined}
      style={{
        ...styles(isMobile).personaSection,
        ...(type === 'image' ? { backgroundImage: `url(${media})` } : {}),
      }}
    >
      {type === 'video' && (
        <video
          src={media}
          autoPlay
          loop
          muted
          playsInline
          style={media.endsWith('.m3u8') ? styles(isMobile).personaVideoContained : styles(isMobile).personaVideoFullWidth}
        />
      )}
      <div
        style={{
          ...styles(isMobile).personaOverlay,
          opacity: visible ? 1 : 0,
          transition: `opacity ${TRANSITION_DURATION} ease`,
        }}
      ></div>
      <div
        style={{
          ...styles(isMobile).personaContent,
          opacity: visible ? 1 : 0,
          transform: visible ? 'translateY(0)' : 'translateY(20px)',
          transition: `opacity ${TRANSITION_DURATION} ease, transform ${TRANSITION_DURATION} ease`,
        }}
      >
        <div>
          <h2 style={styles(isMobile).personaTagline}>{tagline}</h2>
          <p style={styles(isMobile).personaDescription}>{description}</p>
        </div>
      </div>
    </div>
  );
}

export default PersonaSection;

const styles = (isMobile: boolean) => ({
  personaSection: {
    minHeight: '100vh',
    position: 'relative' as const,
    overflow: 'hidden',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    backgroundAttachment: isMobile ? 'scroll' : 'fixed',
    backgroundColor: '#000000',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },

  personaOverlay: {
    position: 'absolute' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(to bottom, rgba(0,0,0,0.2) 0%, rgba(0,0,0,0.4) 50%, rgba(0,0,0,0.2) 100%)',
    zIndex: 1,
  },

  personaContent: {
    position: 'relative' as const,
    zIndex: 2,
    textAlign: 'center' as const,
    width: '100%',
    maxWidth: isMobile ? '300px' : '900px',
    padding: isMobile ? '60px 20px' : '80px 40px',
    background: 'radial-gradient(ellipse at center, rgba(0, 0, 0, 0.2) 0%, rgba(0, 0, 0, 0.1) 40%, rgba(0, 0, 0, 0) 70%)',
    marginTop: '-30px',
  },

  personaTitle: {
    fontSize: isMobile ? '1.2rem' : '1.5rem',
    fontWeight: '400',
    color: 'rgba(255,255,255,0.9)',
    marginBottom: '16px',
    letterSpacing: '2px',
    textTransform: 'uppercase' as const,
  },

  personaTagline: {
    fontSize: isMobile ? '1.8rem' : '2.8rem',
    fontWeight: '800',
    color: 'white',
    marginBottom: '20px',
    lineHeight: '1.2',
    textShadow: '2px 2px 10px rgba(0,0,0,0.5)',
    filter: 'drop-shadow(0px 4px 8px rgba(0,0,0,0.6))',
  },

  personaDescription: {
    fontSize: isMobile ? '1.2rem' : '1.5rem',
    fontWeight: '400',
    color: 'rgba(255,255,255,0.85)',
    lineHeight: '1.6',
    textShadow: '1px 1px 5px rgba(0,0,0,0.5)',
    filter: 'drop-shadow(0px 2px 6px rgba(0,0,0,0.6))',
  },

  personaVideoFullWidth: {
    position: 'absolute' as const,
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover' as const,
    zIndex: 0,
  },

  personaVideoContained: {
    position: 'absolute' as const,
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    height: '90vh',
    width: 'auto',
    objectFit: 'contain' as const,
    zIndex: 0,
  },
});
